import * as R from 'remeda'
import { Stack } from '@mui/material'
import { CustomDataGrid } from '~/ui/components/CustomDataGrid'
import { useGetOrders } from './hooks/useGetOrders'
import { useMemo, useState } from 'react'
import { OrdersRowModel } from './types'
import { useOrderListColumns } from './useOrderListColumns'
import { SimpleDialog } from '~/ui/components/SimpleDialog'
import { useCreateVisitor } from '../visitors/hooks/useCreateVisitor'
import { useGetAgencies } from '../agencies/hooks/useGetAgencies'
import { useGetAgeGroups } from '../visitors/hooks/useGetAgeGroups'
import { useGetEmployees } from '../visitors/hooks/useGetEmployees'
import { useGetInventory } from '../hooks/useGetInventory'
import { format } from 'date-fns'
import { useCreateShopifySales } from './hooks/useCreateShopifySale'
import { useUpdateInventory } from '../visitors/hooks/useUpdateInventory'

export const OrdersList = () => {
  const [visitorCandidate, setVisitorCandidate] = useState<string | null>(null)
  const { data: orders, isLoading } = useGetOrders()

  const { mutate: createVisitor, isPending: isCreatingVisitor } = useCreateVisitor()
  const { mutate: createShopifySales, isPending: isCreatingShopifySales } = useCreateShopifySales()
  const { mutate: updateInventory, isPending: isUpdatingInventory } = useUpdateInventory()

  const isPending = isCreatingVisitor || isCreatingShopifySales || isUpdatingInventory

  const enabled = visitorCandidate !== null

  const variantIds = useMemo(() => {
    const selected = orders.find((o) => o.id === visitorCandidate)

    if (!selected) return []

    return selected?.lineItems.nodes.map((node) => node.variant?.id).filter(R.isNonNullish)
  }, [orders, visitorCandidate])

  const { data: agencies, isLoading: isLoadingAgencies } = useGetAgencies({ enabled })
  const { data: ageGroups, isLoading: isLoadingAgeGroups } = useGetAgeGroups({ enabled })
  const { data: employees, isLoading: isLoadingEmployyes } = useGetEmployees({ enabled })
  const { data: inventory, isLoading: isLoadingInventory } = useGetInventory({
    enabled,
    filters: [
      {
        column: 'product_variant_id',
        operator: 'in',
        value: variantIds,
      },
      { column: 'status', operator: 'eq', value: 'available' },
    ],
  })

  const onlineAgency = agencies?.find((a) =>
    a.agency_name.toLocaleLowerCase().includes('online')
  )?.agency_name

  const visitorToCreate = useMemo(() => {
    const selected = orders.find((o) => o.id === visitorCandidate)

    const product_ids = R.pipe(
      inventory ?? [],
      R.filter((inv) =>
        inv.product_variant_id ? variantIds.includes(inv.product_variant_id) : false
      ),
      R.uniqueBy((el) => el.stock_code_v2)
    )

    const employee = employees?.find((e) =>
      e.full_name.toLocaleLowerCase().includes('yohan')
    )?.full_name
    const age_group = ageGroups?.find((a) => a.name.toLocaleLowerCase().includes('unknown'))?.id
    const country = selected?.shippingAddress?.country
    const total = Number(selected?.totalPriceSet.shopMoney.amount)
    if (
      !visitorCandidate ||
      !onlineAgency ||
      !selected ||
      !country ||
      !product_ids ||
      !employee ||
      !age_group ||
      !total
    )
      return null

    return {
      shopify_order_id: selected.shopify_order_id,
      payload: {
        age_group,
        agency: onlineAgency,
        employee,
        info: 'Customer bought from eshop',
        pax: 1,
        product_ids,
        store: 'wollem',
        products: null,
        has_refund: false,
        payment_type: 'card',
        country,
        total,
        message_id: null,
        created_at: selected.createdAt,
      },
    } as const
  }, [ageGroups, employees, inventory, onlineAgency, orders, variantIds, visitorCandidate])

  const { columns } = useOrderListColumns({
    setVisitorCandidate,
  })

  const rows = useMemo<OrdersRowModel[]>(() => {
    if (!orders) return []

    return orders.map(
      ({
        id,
        totalPriceSet,
        createdAt,
        email,
        lineItems,
        displayFinancialStatus,
        visitor,
        shippingAddress,
        shopify_order_id,
      }) => {
        const { amount } = totalPriceSet.shopMoney

        return {
          id,
          shopify_order_id,
          sales: amount.toString(),
          createdAt,
          email,
          visitor: visitor ? { id: visitor.id, hasRefund: visitor.has_refund } : undefined,
          products: lineItems.nodes.map((p) => p.title).join(', '),
          displayFinancialStatus,
          shippingAddress,
        } as const satisfies OrdersRowModel
      }
    )
  }, [orders])

  const isLoadingDialog =
    isLoadingAgencies || isLoadingAgeGroups || isLoadingEmployyes || isLoadingInventory

  return (
    <Stack overflow="hidden" flex={1}>
      <SimpleDialog
        open={!!visitorCandidate}
        title="Create Visitor"
        text="Are you sure you want to create a visitor?"
        description={
          isLoading
            ? 'Loading...'
            : visitorToCreate
              ? `Date: ${format(visitorToCreate.payload.created_at, 'dd.MM.yyyy')}\nTotal: ${visitorToCreate.payload.total} \nProducts: ${visitorToCreate.payload.product_ids.map((item) => item.stock_code_v2).join(', ')} \n Country: ${visitorToCreate.payload.country}`
              : ''
        }
        isLoading={isLoadingDialog || isPending}
        onSubmit={() => {
          if (visitorToCreate) {
            const productIds = visitorToCreate.payload.product_ids.map((item) => item.id)
            createVisitor(
              {
                ...visitorToCreate.payload,
                product_ids: productIds.map(String),
              },
              {
                onSuccess: (response) => {
                  createShopifySales(
                    { visitor: response.id, shopify_order_id: visitorToCreate.shopify_order_id },
                    {
                      onSuccess: () => {
                        updateInventory(
                          productIds.map((id) => ({
                            id,
                            status: 'sold',
                            sales: response.id,
                          }))
                        )
                        setVisitorCandidate(null)
                      },
                    }
                  )
                },
              }
            )
          }
        }}
        onClose={() => setVisitorCandidate(null)}
      />
      <CustomDataGrid loading={isLoading} rows={rows} columns={columns} />
    </Stack>
  )
}
